import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Users, ShieldCheck, Bot, TrendingUp } from "lucide-react";
import { Stat, AGENT_LABEL } from "./shared";

export function StatsTab({ stats }) {
  if (!stats) return <div className="py-16 text-center text-muted-foreground">Loading stats…</div>;
  const { users, ai } = stats;
  const maxAgent = Math.max(1, ...ai.by_agent.map((a) => a.user_messages));
  const maxTrend = Math.max(1, ...ai.trend.map((t) => t.count));
  return (
    <div className="space-y-6" data-testid="admin-stats">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Total accounts" value={users.total} hint={`+${users.new_7d} this week · +${users.new_30d} this month`} />
        <Stat icon={Bot} label="AI adoption rate" value={`${ai.adoption_rate}%`} hint={`${ai.active_users} users have used the AI`} />
        <Stat icon={TrendingUp} label="Messages sent" value={ai.total_user_messages} hint={`avg ${ai.avg_messages_per_user} / active user`} />
        <Stat icon={ShieldCheck} label="Admins" value={users.admins} hint={`${users.undergraduate} UG · ${users.postgraduate} PG`} />
      </div>

      <Card className="p-6 rounded-2xl border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-extrabold italic text-navy">AI usage by tool</h3>
          <span className="text-xs text-muted-foreground">{ai.total_conversations} conversations · {ai.guest_conversations} as guest</span>
        </div>
        <div className="space-y-3" data-testid="admin-by-agent">
          {ai.by_agent.map((a) => (
            <div key={a.agent} className="flex items-center gap-3">
              <div className="w-32 text-sm text-navy shrink-0">{AGENT_LABEL[a.agent] || a.agent}</div>
              <div className="flex-1 h-6 rounded-full bg-navy/5 overflow-hidden">
                <div className="h-full bg-orange rounded-full transition-all" style={{ width: `${(a.user_messages / maxAgent) * 100}%` }} />
              </div>
              <div className="w-28 text-right text-xs text-muted-foreground shrink-0">{a.user_messages} msgs · {a.conversations} conv.</div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6 rounded-2xl border-border">
        <h3 className="font-display font-extrabold italic text-navy mb-4">Activity (last 14 days)</h3>
        {ai.trend.length === 0 ? (
          <p className="text-sm text-muted-foreground">No activity yet.</p>
        ) : (
          <div className="flex items-end gap-2 h-40" data-testid="admin-trend">
            {ai.trend.map((t) => (
              <div key={t.date} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                <div className="w-full bg-navy rounded-t-md transition-all" style={{ height: `${(t.count / maxTrend) * 100}%`, minHeight: 4 }} title={`${t.count} messages`} />
                <span className="text-[9px] text-muted-foreground truncate w-full text-center">{t.date.slice(5)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-6 rounded-2xl border-border" data-testid="admin-sources">
        <h3 className="font-display font-extrabold italic text-navy mb-1">Conversions by source</h3>
        <p className="text-xs text-muted-foreground mb-4">Guests who arrived from your showcase site (via <code className="text-orange">?utm_source=</code>) and how many created an account.</p>
        {(!stats.sources || stats.sources.length === 0) ? (
          <p className="text-sm text-muted-foreground">No tagged traffic yet. Add a <b>Source</b> to a link in the Tool Links tab.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Guests</TableHead>
                <TableHead className="text-right">Signups</TableHead>
                <TableHead className="text-right">Conversion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.sources.map((s) => (
                <TableRow key={s.source} data-testid={`source-row-${s.source}`}>
                  <TableCell className="font-semibold text-navy">{s.source}</TableCell>
                  <TableCell className="text-right tabular-nums">{s.visitors}</TableCell>
                  <TableCell className="text-right tabular-nums">{s.signups}</TableCell>
                  <TableCell className="text-right tabular-nums text-orange font-semibold">{s.conversion_rate}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
